<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { useLogger } from 'vue-logger-plugin'
const logger = useLogger()
import type {
  IAoeCart,
  IAoeCartEarnedDiscount,
  IAoeCartItem,
} from '../types/CartTypes.ts'
import {
  AddDiscountedItemToCart,
  DeleteItemFromCart,
} from '../services/CartService.ts'
import { SetIsLoading } from '../services/AoeHelpers.ts'
import { handleError } from '../services/ErrorService.ts'
import { ref } from 'vue'
import { onMounted, watch } from 'vue';




const emit = defineEmits(["update-cart", "free-gift-opted-out", "free-gift-selected", "show-free-gifts-again"]);

const props = defineProps<{
  cart: IAoeCart
  earnedDiscount: IAoeCartEarnedDiscount
  GetCartItemsByPoeDiscPlanLevelDiscId(
    poeDiscPlanLevelDiscIdKey: number,
  ): IAoeCartItem[]
  FindCartItem(
    poeDiscPlanLevelDiscId: number,
    skuCode: string,
  ): IAoeCartItem | null
  GetFullProductImageUrlFromStringDictionary(productCode: string): string
}>()

onMounted(() => {
  checkAndEmitFreeGiftSelected();
});


watch(
  () => [props.cart, props.earnedDiscount],
  () => {
    checkAndEmitFreeGiftSelected();
  },
  { immediate: true, deep: true }
);

function checkAndEmitFreeGiftSelected() {
  const selected = (props.earnedDiscount.items ?? []).some(item =>
    props.FindCartItem(props.earnedDiscount.poeDiscPlanLevelDiscId, item.skuCode)
  );
  if (selected) {
    emit('free-gift-selected');
  }
}

const showFreeGiftOptions = ref(true)
const isOptedOut = ref(false)

async function DeselectFreeProductClickedHandler(
  poeDiscPlanLevelDiscId: number,
) {
  logger.debug('DeselectFreeProductClickedHandler')
  const cartItems = props.GetCartItemsByPoeDiscPlanLevelDiscId(
    poeDiscPlanLevelDiscId,
  )
  let previousSelectedCartItem: IAoeCartItem | null = null
  if (cartItems && cartItems.length > 0) {
    previousSelectedCartItem = cartItems[0]
  }
  if (previousSelectedCartItem) {
    SetIsLoading(true)
    DeleteItemFromCart({
      aoeCartIdToken: props.cart.aoeCartIdToken,
      lineNumber: previousSelectedCartItem.lineNumber,
    }).catch(
        async error => {
          await handleError(error, t)
        },
      ).finally(() => {
        SetIsLoading(false)
        emit("update-cart", null)
       emit("free-gift-opted-out")
       isOptedOut.value = true
       showFreeGiftOptions.value = false
      })
  } else {
    emit("free-gift-opted-out")
    isOptedOut.value = true
    showFreeGiftOptions.value = false
  }
}


function showFreeGiftsAgain() {
  isOptedOut.value = false
  showFreeGiftOptions.value = true
  emit("show-free-gifts-again")
}

async function SelectFreeProductClickedHandler(
  poeDiscPlanLevelDiscId: number,
  skuCode: string,
) {
  logger.debug('selectFreeProductClickedHandler')
  if (!props.cart) return
  const cartItem = props.FindCartItem(poeDiscPlanLevelDiscId, skuCode)
  let qty = props.earnedDiscount.quantity;
  if (cartItem) {
    logger.debug('RemoveDiscountedItemFromCart')
    if (cartItem.skuCode === skuCode) {
      logger.debug('Cart item already selected')
      return
    }
  }

  const cartItems = props.GetCartItemsByPoeDiscPlanLevelDiscId(
    poeDiscPlanLevelDiscId,
  )
  let previousSelectedCartItem: IAoeCartItem | null = null
  if (cartItems && cartItems.length > 0) {
    qty = cartItems[0].quantity;
    previousSelectedCartItem = cartItems[0]
  }
  SetIsLoading(true)

  AddDiscountedItemToCart(
    props.cart.aoeCartIdToken,
    { skuCode, quantity: qty, poeDiscPlanLevelDiscId },
    previousSelectedCartItem?.lineNumber,
  ).catch(
        async error => {
          await handleError(error, t)
        },
      ).finally(() => {
        SetIsLoading(false)
        emit("update-cart", null)
        emit("free-gift-selected")
      })
}
</script>
<template>
  <div
    class="col-12 col-md-6 discountMessage"
    v-html="props.earnedDiscount.awardDescription"
  ></div>
  <div class="col-12 col-md-6 unselect-select-free-product">
    <KButton
      v-if="!isOptedOut"
      :fill-mode="'link'"
      @click="
        DeselectFreeProductClickedHandler(earnedDiscount.poeDiscPlanLevelDiscId)
      "
      >{{ t('cart.NoFreeGift') }}</KButton
    >
    <KButton
      v-else
      :fill-mode="'link'"
      @click="showFreeGiftsAgain"
    >
      {{ t('cart.ShowFreeGift') }}
    </KButton>
  </div>
  <div class="row freeItemListOptions" v-if="showFreeGiftOptions">
    <div
      class="freeOption col border rounded"
      :class="{
        selected: props.FindCartItem(
          earnedDiscount.poeDiscPlanLevelDiscId,
          item.skuCode,
        ),
      }"
      v-for="item in earnedDiscount.items"
      :key="item.skuCode"
    >
      <div
        class="select-free-product"
        @click="
          SelectFreeProductClickedHandler(
            earnedDiscount.poeDiscPlanLevelDiscId,
            item.skuCode,
          )
        "
      >
        <div
          v-if="
            props.FindCartItem(
              earnedDiscount.poeDiscPlanLevelDiscId,
              item.skuCode,
            )
          "
          class="row selectedLabelDiv"
        >
          <div class="col-1 checkMark">&#10004;</div>
          <div class="col-8">{{ t('cart.SelectedFreeGift') }}</div>
        </div>
        <div
          v-if="
            props.FindCartItem(
              earnedDiscount.poeDiscPlanLevelDiscId,
              item.skuCode,
            ) == null
          "
          class="row selectedLabelDiv"
        >
          <div class="col-1 notSelected">&#10004;</div>
          <div class="col-10"></div>
        </div>
        <img
          :src="
            props.GetFullProductImageUrlFromStringDictionary(item.productCode)
          "
          class="img-fluid prodImage"
          :alt="t('cart.ProductImageAlt')"
        />
        <div>{{ item.skuName }}</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
 .freeItemListOptions {
    margin: $spacing-2-5 !important;
    display: grid !important;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: $spacing-2-5;
    width: 100%;
  }
</style>
